import { useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { creativeProcess } from '../../../mocks/altaCostura';

export default function ProcessSection() {
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.1, distance: 36, duration: 900 });
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section ref={ref} className="w-full bg-brand-gray-50 py-20 md:py-32">
      <div className="px-6 lg:px-[7%]">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto" style={style}>
          <span className="font-sans text-brand-gray-400 text-[11px] tracking-[0.2em] uppercase font-medium">
            Proceso Creativo
          </span>
          <h2 className="font-serif text-brand-black text-[clamp(2.5rem,5vw,4rem)] font-semibold leading-[1.1] mt-4">
            De la Idea
            <span className="italic text-brand-gray-300"> al Vestido</span>
          </h2>
          <p className="font-sans text-brand-gray-500 text-[15px] leading-[1.8] mt-6">
            Cada prenda de alta costura es un viaje de creación que puede durar semanas.
            Te acompañamos en cada paso del proceso.
          </p>
        </div>

        {/* Steps Navigation */}
        <div
          className="mt-16 md:mt-20 flex flex-wrap justify-center gap-3 md:gap-4"
          style={isVisible ? {
            opacity: 1,
            transform: 'translateY(0px)',
            transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
          } : {
            opacity: 0,
            transform: 'translateY(20px)',
            transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 200ms',
            willChange: 'opacity, transform',
          }}
        >
          {creativeProcess.map((step, index) => (
            <button
              key={step.step}
              onClick={() => setActiveStep(index)}
              className={`relative font-sans text-sm tracking-wider px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap ${
                index === activeStep
                  ? 'bg-brand-black text-white'
                  : 'bg-white text-brand-gray-500 hover:text-brand-black border border-brand-gray-200'
              }`}
            >
              <span className="mr-2 text-xs opacity-60">{String(step.step).padStart(2, '0')}</span>
              {step.title}
            </button>
          ))}
        </div>

        {/* Active Step Detail */}
        <div
          className="mt-12 md:mt-16"
          style={isVisible ? {
            opacity: 1,
            transform: 'translateY(0px)',
            transition: 'opacity 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 350ms, transform 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 350ms',
          } : {
            opacity: 0,
            transform: 'translateY(20px)',
            transition: 'opacity 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 350ms, transform 700ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 350ms',
            willChange: 'opacity, transform',
          }}
        >
          <div className="bg-white rounded-[32px] p-8 md:p-16 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
              {/* Step Number */}
              <div className="flex-shrink-0">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-brand-gray-50 flex items-center justify-center">
                  <i className={`${creativeProcess[activeStep].icon} text-brand-black text-2xl md:text-3xl`} />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-brand-gray-300 text-5xl md:text-6xl font-light">
                    {String(creativeProcess[activeStep].step).padStart(2, '0')}
                  </span>
                  <h3 className="font-serif text-brand-black text-2xl md:text-3xl font-medium">
                    {creativeProcess[activeStep].title}
                  </h3>
                </div>
                <p className="font-sans text-brand-gray-500 text-[16px] md:text-[17px] leading-[1.8] mt-6">
                  {creativeProcess[activeStep].description}
                </p>

                {/* Progress indicator */}
                <div className="mt-10 flex items-center gap-3">
                  {creativeProcess.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        i === activeStep
                          ? 'w-12 bg-brand-black'
                          : i < activeStep
                            ? 'w-6 bg-brand-gray-300'
                            : 'w-6 bg-brand-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step numbers bottom */}
        <div className="mt-12 flex justify-center gap-2">
          {creativeProcess.map((step, i) => (
            <button
              key={step.step}
              onClick={() => setActiveStep(i)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                i === activeStep ? 'bg-brand-black scale-125' : 'bg-brand-gray-200 hover:bg-brand-gray-300'
              }`}
              aria-label={`Paso ${step.step}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}